import { format, isValid } from "date-fns";
import React, { useCallback, useMemo } from "react";

import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

// components
import HorizontalPercentageBar from "../../../../../../components/HorizontalPercentageBar";
import Text from "../../../../../../components/Text";
import TruncatedText from "../../../../../../components/Text/TruncatedText";
import Tooltip from "../../../../../../components/Tooltip";

// store
import useStore from "../../../../store";

import {
	useCohorts,
	useNPSAdditionalFeedback,
	usePageSettings,
	useQuestionsSummary,
} from "../../../../../../api/hooks";

import { getEngagementSurveyQuestionAnswers } from "../../../../../../api/services";

import downloadCSV from "../../../../../../utils/downloadCSV";
import escapeCsvField from "../../../../../../utils/escapeCsvField";
// utils
import { getColor } from "../../../../../../utils/getColor";

// config
import Config from "../../../../Config";
// css
import css from "./QuestionsTable.module.scss";

type DistributionData = {
	total_response_count: number;
	score_to_count: Record<1 | 2 | 3 | 4 | 5, number>;
};
export type SortableCol = "factor" | "average_score" | "total_response_count" | "question_text";

export type QuestionRow = {
	id: string;
	factor: React.ReactNode;
	question_text: React.ReactNode;
	average_score: React.ReactNode;
	distribution: React.ReactNode;
	total_response_count: React.ReactNode;
	question_id: string;
	scale: string;
};

const { DEFAULT_COHORT_SELECTION, DEFAULT_FACTOR_SELECTION, QUESTION_COLUMN_WIDTH, DISTRIBUTION_COLUMN_WIDTH } =
	Config.QUESTIONS.TABLE;
const PERCENTAGE_BAR_WIDTH = DISTRIBUTION_COLUMN_WIDTH - 40;
const MIN_SECTION_PIXELS = 32;
const REPLACEMENT_SECTION_PIXELS = 16;
/**
 * Hook to make an api call based on changes the engagement date range filter state
 * and return rows formatted for the QuestionsTable component
 */
const useQuestionTableRows = (
	sortField: SortableCol,
	sortDirection: "desc" | "asc",
	searchTerm: string
): {
	rows: QuestionRow[];
	isLoading: boolean;
	getCSVContent: () => string | undefined;
	selectCSVDownloadName: () => string;
	handleDownloadQuestionAnswers: (questionId: string) => void;
	getRowInformation: (questionId: string) => {
		questionId: string;
		filter: {
			startDate: Date | null;
			endDate: Date | null;
			value: string;
		};
		cohortId: string;
	};
	handleDownloadNPSAdditionalFeedback: () => void;
} => {
	const [filter] = useStore((state) => [state.filter]);
	const { data: pageSettingsData } = usePageSettings();
	const { data: cohortData } = useCohorts();
	const selectedCohort = pageSettingsData?.questions?.allQuestionsTable?.cohort || DEFAULT_COHORT_SELECTION;
	const selectedFactor = pageSettingsData?.questions?.allQuestionsTable?.factor || DEFAULT_FACTOR_SELECTION;

	const cohortName = useMemo(() => {
		const cohortOptions = [{ _id: "all", name: "All Employees" }, ...(cohortData?.cohorts || [])];
		return cohortOptions.find((c) => c._id === selectedCohort)?.name;
	}, [selectedCohort, cohortData]);

	const { data: questionsData, isLoading } = useQuestionsSummary({
		startDate: filter.startDate,
		endDate: filter.endDate,
		cohortIds: selectedCohort !== "all" ? selectedCohort : undefined,
		factor: selectedFactor !== "All Factors" ? selectedFactor : undefined,
		search: searchTerm,
		sort: `${sortDirection}(${sortField})`,
	});

	const { data: npsAdditionalFeedbackData } = useNPSAdditionalFeedback({
		startDate: filter.startDate,
		endDate: filter.endDate,
		cohortIds: selectedCohort !== "all" ? selectedCohort : undefined,
	});

	const wrapQuestion = (question, textColor) => {
		return (
			<div className={css.questionWrapper}>
				<TruncatedText
					color={textColor}
					text={question.question_text}
					maxWidth={QUESTION_COLUMN_WIDTH}
					size="paragraph-3"
					weight="regular"
				/>
				{!question.enabled && (
					<span className={css.infoIcon}>
						<Tooltip
							message={"This question has been disabled and is not being included in any future surveys."}
						>
							<FontAwesomeIcon icon={faCircleInfo} />
						</Tooltip>
					</span>
				)}
			</div>
		);
	};

	const formatText = (text, textColor) => {
		return (
			<Text size="paragraph-3" weight="regular" color={textColor}>
				{text}
			</Text>
		);
	};

	const colorMap = {
		1: getColor("primary", 2),
		2: getColor("primary", 3),
		3: getColor("primary", 4),
		4: getColor("primary", 7),
		5: getColor("primary", 9),
	};

	const scoreMap = {
		1: "Strongly Disagree",
		2: "Disagree",
		3: "Neither Agree nor Disagree",
		4: "Agree",
		5: "Strongly Agree",
	};

	const blankSections = [
		{
			key: "1",
			percentage: 100,
			color: getColor("neutral", 2),
		},
	];

	const scoresToPercentages = (distributionData: DistributionData): Record<1 | 2 | 3 | 4 | 5, number> => {
		const { total_response_count, score_to_count } = distributionData;
		return Object.entries(score_to_count).reduce(
			(acc, [score, count]) => {
				acc[score] = (count / total_response_count) * 100;
				return acc;
			},
			{ 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 }
		);
	};

	/**
	 * Returns adjusted percentages and text for each section of the percentage bar.
	 * after any percentages under min are considered,
	 * the remaining percentages must be recalculated for the remaining sections.
	 */
	const processSections = (scoresMap: Record<1 | 2 | 3 | 4 | 5, number>) => {
		// the minimum percentage for full text converted from minimum pixels specified in design
		const minFullTextPercent = (MIN_SECTION_PIXELS / PERCENTAGE_BAR_WIDTH) * 100;
		// the minimum pecentage for width converted from pixels specified in design used to replace section percentages
		const replacementPercent = (REPLACEMENT_SECTION_PIXELS / PERCENTAGE_BAR_WIDTH) * 100;

		// sections that are being replaced mapped to an array of scores
		const inadequateSections = Object.entries({ ...scoresMap })
			.filter(([, percentage]) => percentage < replacementPercent && percentage > 0)
			.map((section) => section[0]);

		// total percentage of all sections being replaced
		const inadequateTotalPercent = inadequateSections.reduce((acc, score) => acc + scoresMap[score], 0);
		// the total percentage of all replacement sections we will add
		const replacementTotalPercent = inadequateSections.length * replacementPercent;
		// the remaining percentage after replacement is accounted for
		const remainingPercentage = 100 - replacementTotalPercent + inadequateTotalPercent;

		// map and calculate new percentages for each section
		return [1, 2, 3, 4, 5].map((score) => {
			const percentage = scoresMap[score];
			const inadequate = inadequateSections.includes(score.toString());
			// round up to 1% if less than 1%
			const wholeNumberPercentage = Math.max(Math.round(percentage), 1);
			const shortenText = wholeNumberPercentage <= minFullTextPercent;
			const recalculatedPercentage = inadequate
				? // replace percentages for inadequate sections
					replacementPercent
				: // recalculate percentages for remaining sections
					(percentage / 100) * remainingPercentage;
			// display only the score if the percentage is inadequate
			const text = shortenText ? `${score}` : `${score} (${wholeNumberPercentage}%)`;
			const hoverEffects: "grow"[] = ["grow"];
			return {
				percentage: recalculatedPercentage,
				color: colorMap[score],
				text,
				// the actual percentage is always displayed in the tooltip
				tooltipText: [`${scoreMap[score]} (${score})`, `${wholeNumberPercentage}% of Responses`],
				hoverEffects,
				key: `${score}`,
			};
		});
	};

	const formatPercentageBar = (distributionData: DistributionData) => {
		const scoresMap = scoresToPercentages(distributionData);
		const sections =
			distributionData.total_response_count > 0 && Object.keys(distributionData.score_to_count).length > 0
				? processSections(scoresMap)
				: blankSections;
		return <HorizontalPercentageBar sections={sections} fullWidth={PERCENTAGE_BAR_WIDTH} />;
	};

	const questionRows: QuestionRow[] = useMemo(() => {
		return (
			questionsData?.data.map((question) => {
				const averageScore = question.average_score ? parseFloat(question.average_score.toFixed(1)) : "-";
				const distribution = formatPercentageBar(question.distribution as DistributionData);
				const textColor = question.enabled ? "neutral-9" : "neutral-3";
				return {
					id: question.question_text,
					factor: formatText(question.factor, textColor),
					question_text: wrapQuestion(question, textColor),
					average_score: formatText(averageScore, textColor),
					distribution: distribution,
					total_response_count: formatText(question.distribution.total_response_count, textColor),
					question_id: question._id,
					scale: question.scale,
				};
			}) || []
		);
	}, [questionsData]);

	const selectCSVDownloadName = useCallback(() => {
		const date = new Date().toDateString();
		const timePeriod = Config.FILTER_OPTIONS.find((option) => option.value === filter.value)?.label;
		const middleText = `${cohortName}${searchTerm.length ? `-Search By '${searchTerm}'` : ""}`;

		return `PerformYard-Engagement-Questions-${timePeriod}-${middleText}-${date}.csv`;
	}, [cohortName, searchTerm, filter.value]);

	const getCSVContent = () => {
		if (!questionsData) return;
		const csvColumns = [
			"Factor",
			"Question Text",
			"# of Responses",
			"Average Score",
			"Strongly Disagree (1)",
			"Disagree (2)",
			"Neither Agree nor Disagree (3)",
			"Agree (4)",
			"Strongly Agree (5)",
		];
		const csvRows = questionsData.data.map((question) => {
			const scoresMap = scoresToPercentages(question.distribution as DistributionData);
			return [
				escapeCsvField(question.factor),
				escapeCsvField(question.question_text),
				question.distribution.total_response_count,
				question.average_score ? parseFloat(question.average_score.toFixed(1)) : "",
				...[1, 2, 3, 4, 5].map((score) => {
					if (scoresMap[score] === 0) return "";
					// round up to 1% if less than 1%
					const wholeNumberPercentage = Math.max(Math.round(scoresMap[score]), 1);
					return `${wholeNumberPercentage}%`;
				}),
			];
		});
		return [csvColumns, ...csvRows].map((row) => row.join(",")).join("\n");
	};

	const handleDownloadQuestionAnswers = async (questionId) => {
		const { data = [] } = await getEngagementSurveyQuestionAnswers(
			questionId,
			filter.startDate || undefined,
			filter.endDate || undefined,
			selectedCohort !== "all" ? selectedCohort : undefined
		);
		const questionText = questionsData?.data?.find((question) => question._id === questionId)?.question_text || "";
		const cohortMap = cohortData?.cohorts?.reduce((acc, cohort) => {
			acc[cohort._id] = cohort.name;
			return acc;
		}, {});
		const csvColumns = ["Question Text", "Answer", "Cohorts"].join(",") + "\n";

		const csvContent = data
			.filter((row) => row.value !== "")
			.map((row) => {
				const questionTextEscaped = escapeCsvField(questionText);
				const valueEscaped = escapeCsvField(String(row.value));
				const cohortsEscaped = escapeCsvField(
					String(row.cohort_ids.map((cohortId) => cohortMap?.[cohortId] || "").join("; "))
				);
				return `${questionTextEscaped},${valueEscaped},${cohortsEscaped}`;
			})
			.join("\n");

		const date = new Date().toDateString();
		const timePeriod = Config.FILTER_OPTIONS.find((option) => option.value === filter.value)?.label || "";

		const filename = `PerformYard-Engagement-Question-${questionText}-${timePeriod}-${cohortName}-${date}.csv`;

		downloadCSV(csvColumns + csvContent, filename);
	};

	const getRowInformation = (questionId) => {
		return {
			questionId,
			filter: {
				startDate: filter.startDate,
				endDate: filter.endDate,
				value: filter.value,
			},
			cohortId: selectedCohort,
		};
	};

	const handleDownloadNPSAdditionalFeedback = useCallback(() => {
		const additionalFeedback = npsAdditionalFeedbackData?.data;
		const cohortMap = cohortData?.cohorts?.reduce(
			(acc, cohort) => {
				acc[cohort._id] = cohort.name;
				return acc;
			},
			{} as Record<string, string>
		);

		const getCSVContent = () => {
			const csvColumns = ["eNPS Score", "Additional Feedback", "Cohorts", "Response Date"];

			const csvRows = additionalFeedback?.map((feedback) => {
				const submittedAtDate = new Date(feedback.submitted_at);
				let formattedSubmittedAtDate = "";
				if (isValid(submittedAtDate)) {
					formattedSubmittedAtDate = format(submittedAtDate, "dd-MMM-yyyy");
				} else {
					formattedSubmittedAtDate = "";
				}
				const cohorts = escapeCsvField(
					(feedback.cohort_ids || [])
						.map((cohortId) => cohortMap?.[cohortId] || "")
						.filter(Boolean)
						.sort((a, b) => a.localeCompare(b))
						.join("; ")
				);
				return [
					feedback.enps_value,
					escapeCsvField(feedback.additional_feedback),
					cohorts,
					formattedSubmittedAtDate,
				];
			}) || [["", "", "", ""]];

			return [csvColumns, ...csvRows].join("\n");
		};

		const date = new Date().toDateString();
		const timePeriod =
			Config.FILTER_OPTIONS.find((option) => option.value === filter.value)?.label ||
			(filter.startDate && filter.endDate
				? filter.startDate.toDateString() + " to " + filter.endDate.toDateString()
				: "");
		const filename = `PerformYard-Engagement-eNPS-AdditionalFeedback-${timePeriod}-${cohortName}-${date}.csv`;
		const csvContent = getCSVContent();

		downloadCSV(csvContent, filename);
	}, [npsAdditionalFeedbackData, filter.value, filter.startDate, filter.endDate, cohortName, cohortData]);

	return {
		rows: questionRows,
		isLoading,
		getCSVContent,
		selectCSVDownloadName,
		handleDownloadQuestionAnswers,
		getRowInformation,
		handleDownloadNPSAdditionalFeedback,
	};
};

export default useQuestionTableRows;
